import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { COLOR_VALUE, DENTIN_TYPE } from './constants'

export interface IndicationFormData {
  [FORM_FIELDS.RECIPE_TYPE]: string
  [FORM_FIELDS.INDICATION]: string
}

export interface MaterialFormData {
  [FORM_FIELDS.BRAND]: string
  [FORM_FIELDS.NATURAL_DIE_MATERIAL]: string
  [FORM_FIELDS.CAPBRAND]: string
  [FORM_FIELDS.STAININGBRAND]: string
  [FORM_FIELDS.AVAILSPACE_CERVICAL]: number
  [FORM_FIELDS.AVAILSPACE_MIDDLE]: number
  [FORM_FIELDS.AVAILSPACE_INCISAL]: number
}

export interface MeasureColorFormData {
  [FORM_FIELDS.PREP_COLOR]: string
  [FORM_FIELDS.DENTIN_COLOR]: string
  [FORM_FIELDS.PREPCOLOR_CERVICAL]?: string[]
  [FORM_FIELDS.PREPCOLOR_MIDDLE]: string[]
  [FORM_FIELDS.PREPCOLOR_INCISAL]?: string[]
  [FORM_FIELDS.TARGETCERVICAL]?: string[]
  [FORM_FIELDS.TARGETINCISAL]: string[]
  [FORM_FIELDS.TARGETMIDDLE]?: string[]
  [FORM_FIELDS.SKINENAMELTYPE]: string
}

export type FormData = IndicationFormData &
  MaterialFormData &
  MeasureColorFormData

export const enum FORM_FIELDS {
  PROTOCOL = 'protocol',
  INDICATION = 'indication',
  RECIPE_TYPE = 'recipe_type',
  STAININGBRAND = 'stainingbrand',
  NATURAL_DIE_MATERIAL = 'natural_die_material',
  CAPBRAND = 'capbrand',
  BRAND = 'brand',
  AVAILSPACE_CERVICAL = 'availspace_cervical',
  AVAILSPACE_MIDDLE = 'availspace_middle',
  AVAILSPACE_INCISAL = 'availspace_incisal',
  PREPCOLOR_CERVICAL = 'prepcolor_cervical',
  PREPCOLOR_MIDDLE = 'prepcolor_middle',
  PREPCOLOR_INCISAL = 'prepcolor_incisal',
  TARGETCERVICAL = 'targetcervical',
  TARGETMIDDLE = 'targetmiddle',
  TARGETINCISAL = 'targetincisal',
  SKINENAMELTYPE = 'skinenameltype',

  // Extra Fields
  SKINENAMELTYPE_CERVICAL = 'skinenameltype_cervical',
  SKINENAMELTYPE_MIDDLE = 'skinenameltype_middle',
  SKINENAMELTYPE_INCISAL = 'skinenameltype_incisal',
  PREP_COLOR = 'prepColor',
  DENTIN_COLOR = 'dentinColor',
}

export const enum SHOW_PREPARATION_COLOR {
  LIGHT = 'Crown',
  MEDIUM = 'Full Monolithic Crown',
  DARK = 'Veneer on Lithium Disilicate',
  CUSTOM = 'Veneer on refractory',
}

/**
 * Get validation schema with i18n error messages
 */
export const validationSchema = () => {
  const { t } = useTranslation()

  return yup.object().shape({
    [FORM_FIELDS.RECIPE_TYPE]: yup
      .string()
      .required(t('validation.recipeOption')),
    [FORM_FIELDS.INDICATION]: yup.string().required(t('validation.indication')),
    [FORM_FIELDS.BRAND]: yup.string().required(t('validation.ceramicBrand')),
    [FORM_FIELDS.NATURAL_DIE_MATERIAL]: yup.string().optional(),
    [FORM_FIELDS.CAPBRAND]: yup.string().when('$requiredInputValidation', {
      is: (val: any) => val?.[FORM_FIELDS.CAPBRAND],
      then: (schema) => schema.required(t('validation.framework')),
      otherwise: (schema) => schema.optional(),
    }),

    [FORM_FIELDS.STAININGBRAND]: yup.string().optional(),

    [FORM_FIELDS.AVAILSPACE_CERVICAL]: yup
      .number()
      .when(FORM_FIELDS.RECIPE_TYPE, {
        is: DENTIN_TYPE.TRIPLE_DENTIN,
        then: (schema) => schema.required(t('validation.cervicalValue')),
        otherwise: (schema) => schema.optional(),
      }),

    [FORM_FIELDS.AVAILSPACE_MIDDLE]: yup
      .number()
      .required(t('validation.middleValue')),

    [FORM_FIELDS.AVAILSPACE_INCISAL]: yup
      .number()
      .when(FORM_FIELDS.RECIPE_TYPE, {
        is: DENTIN_TYPE.TRIPLE_DENTIN,
        then: (schema) => schema.required(t('validation.incisalValue')),
        otherwise: (schema) => schema.optional(),
      }),

    [FORM_FIELDS.PREPCOLOR_MIDDLE]: yup.mixed().when(FORM_FIELDS.INDICATION, {
      is: (indication: string) =>
        Object.values(SHOW_PREPARATION_COLOR).includes(indication),
      then: () =>
        yup.mixed().when(FORM_FIELDS.PREP_COLOR, {
          is: COLOR_VALUE.LabColor,
          then: () =>
            yup
              .array()
              .of(yup.number().typeError(t('validation.number')).required())
              .required(t('validation.preparationMiddle')),
          otherwise: () =>
            yup.string().required(t('validation.preparationMiddle')),
        }),
    }),

    [FORM_FIELDS.PREPCOLOR_CERVICAL]: yup.mixed().when(FORM_FIELDS.INDICATION, {
      is: (indication: string) =>
        Object.values(SHOW_PREPARATION_COLOR).includes(indication),
      then: () =>
        yup.mixed().when(FORM_FIELDS.RECIPE_TYPE, {
          is: DENTIN_TYPE.TRIPLE_DENTIN,
          then: () =>
            yup.mixed().when(FORM_FIELDS.PREP_COLOR, {
              is: COLOR_VALUE.LabColor,
              then: () =>
                yup
                  .array()
                  .of(yup.number().typeError(t('validation.number')))
                  .required(t('validation.preparationCervical')),
              otherwise: () =>
                yup.string().required(t('validation.preparationCervical')),
            }),
        }),
    }),

    [FORM_FIELDS.PREPCOLOR_INCISAL]: yup.mixed().when(FORM_FIELDS.INDICATION, {
      is: (indication: string) =>
        Object.values(SHOW_PREPARATION_COLOR).includes(indication),
      then: () =>
        yup.mixed().when(FORM_FIELDS.RECIPE_TYPE, {
          is: DENTIN_TYPE.TRIPLE_DENTIN,
          then: () =>
            yup.mixed().when(FORM_FIELDS.PREP_COLOR, {
              is: COLOR_VALUE.LabColor,
              then: () =>
                yup
                  .array()
                  .of(yup.number().typeError(t('validation.number')))
                  .required(t('validation.preparationIncisal')),
              otherwise: () =>
                yup.string().required(t('validation.preparationIncisal')),
            }),
        }),
    }),

    [FORM_FIELDS.TARGETMIDDLE]: yup.mixed().when(FORM_FIELDS.DENTIN_COLOR, {
      is: COLOR_VALUE.LabColor,
      then: () =>
        yup
          .array()
          .of(yup.number().typeError(t('validation.number')).required())
          .required(t('validation.targetMiddle')),
      otherwise: () => yup.string().required(t('validation.targetMiddle')),
    }),

    [FORM_FIELDS.TARGETCERVICAL]: yup.mixed().when(FORM_FIELDS.RECIPE_TYPE, {
      is: DENTIN_TYPE.TRIPLE_DENTIN,
      then: () =>
        yup.mixed().when(FORM_FIELDS.DENTIN_COLOR, {
          is: COLOR_VALUE.LabColor,
          then: () =>
            yup
              .array()
              .of(
                yup
                  .mixed()
                  .test(
                    'not-null',
                    t('validation.targetCervical'),
                    (val) => val !== null && val !== undefined,
                  ),
              )
              .required(t('validation.targetCervical')),
          otherwise: () =>
            yup.string().required(t('validation.targetCervical')),
        }),
    }),

    [FORM_FIELDS.TARGETINCISAL]: yup.mixed().when(FORM_FIELDS.RECIPE_TYPE, {
      is: DENTIN_TYPE.TRIPLE_DENTIN,
      then: () =>
        yup.mixed().when(FORM_FIELDS.DENTIN_COLOR, {
          is: COLOR_VALUE.LabColor,
          then: () =>
            yup
              .array()
              .of(
                yup
                  .mixed()
                  .test(
                    'not-null',
                    t('validation.targetIncisal'),
                    (val) => val !== null && val !== undefined,
                  ),
              )
              .required(t('validation.targetIncisal')),
          otherwise: () => yup.string().required(t('validation.targetIncisal')),
        }),
    }),

    [FORM_FIELDS.SKINENAMELTYPE]: yup
      .string()
      .required(t('validation.enamelType')),

    [FORM_FIELDS.PREP_COLOR]: yup.string().optional(),
    [FORM_FIELDS.DENTIN_COLOR]: yup.string().optional(),
  })
}
